"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../../config"
import { useLanguage } from "../../context/LanguageContext"
import { useNotification } from "../../context/NotificationContext"
import "./UserManagement.css"

const UserManagement = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortField, setSortField] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState("desc")

  // Fetch users on component mount and when pagination/sorting changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true)
        setError(null)

        const res = await axios.get(
          `${API_URL}/api/admin/users?page=${currentPage}&sort=${sortField}&order=${sortOrder}&search=${searchTerm}`,
        )
        setUsers(res.data)
        setTotalPages(res.data.totalPages)
        setLoading(false)
      } catch (err) {
        console.error(err)
        setError("Failed to load users")
        setLoading(false)
      }
    }

    fetchUsers()
  }, [currentPage, sortField, sortOrder, searchTerm])

  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      // Set new sort field and default to ascending
      setSortField(field)
      setSortOrder("asc")
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    // Reset to first page when searching
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleStatusChange = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { isActive })

      // Update user in state
      setUsers(users.map((user) => (user._id === userId ? { ...user, isActive } : user)))

      showNotification(isActive ? t("User activated successfully") : t("User deactivated successfully"), "success")
    } catch (err) {
      console.error(err)
      showNotification(t("Failed to update user status"), "error")
    }
  }

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { role })

      // Update user in state
      setUsers(users.map((user) => (user._id === userId ? { ...user, role } : user)))

      showNotification(t("User role updated successfully"), "success")
    } catch (err) {
      console.error(err)
      showNotification(t("Failed to update user role"), "error")
    }
  }
  console.log(users)

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t("Are you sure you want to delete this user?"))) {
      return
    }

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`)

      // Remove user from state
      setUsers(users.filter((user) => user._id !== userId))
      showNotification(t("User deleted successfully"), "success")
    } catch (err) {
      console.error(err)
      showNotification(t("Failed to delete user"), "error")
    }
  }

  if (loading && users.length === 0) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>{t("Loading users...")}</p>
      </div>
    )
  }

  return (
    <div className="user-management-container" dir={direction}>
      <div className="user-management-header">
        <h1>{t("User Management")}</h1>
        <p className="subtitle">{t("Manage user accounts and permissions")}</p>
      </div>

      <div className="user-management-actions">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t("Search users...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            <i className="fas fa-search"></i>
          </button>
        </form>

        {/* <Link to="/admin/users/new" className="add-user-button">
          <i className="fas fa-user-plus"></i> {t("Add User")}
        </Link> */}
      </div>

      {error ? (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {t(error)}
        </div>
      ) : (
        <>
          <div className="users-table-container">
            {users.length === 0 ? (
              <p className="no-data">{t("No users found")}</p>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("name")} className="sortable-header">
                      {t("Name")}
                      {sortField === "name" && <i className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"}`}></i>}
                    </th>
                    <th onClick={() => handleSort("email")} className="sortable-header">
                      {t("Email")}
                      {sortField === "email" && (
                        <i className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"}`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort("createdAt")} className="sortable-header">
                      {t("Joined")}
                      {sortField === "createdAt" && (
                        <i className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"}`}></i>
                      )}
                    </th>
                    <th>{t("Role")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <Link to={`/admin/users/${user._id}`} className="user-name-link">
                          {user.name}
                        </Link>
                      </td>
                      <td>{user.email}</td>
                      <td>{new Date(user.date).toLocaleDateString()}</td>
                      <td>
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user._id, e.target.value)}
                          className="role-select"
                        >
                          <option value="user">{t("User")}</option>
                          <option value="admin">{t("Admin")}</option>
                        </select>
                      </td>
                      <td>
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id={`status-${user._id}`}
                            checked={user.isActive}
                            onChange={(e) => handleStatusChange(user._id, e.target.checked)}
                          />
                          <label htmlFor={`status-${user._id}`}></label>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link to={`/admin/users/${user._id}`} className="view-button" title={t("View User")}>
                            <i className="fas fa-eye"></i>
                          </Link>
                          <Link to={`/admin/users/${user._id}/edit`} className="edit-button" title={t("Edit User")}>
                            <i className="fas fa-edit"></i>
                          </Link>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteUser(user._id)}
                            title={t("Delete User")}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-button ${currentPage === page ? "active" : ""}`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default UserManagement
